<template>
  <BaseCardSection title="Základní údaje" :is-visible="true" @validation="handleValidation">
    <div class="row g-3">
      <!-- 1. Název kalkulace -->
      <div class="col-md-12">
        <label class="form-label fw-bold">Název kalkulace: <small class="text-muted">({{
            nameLength
          }}/120)</small></label>
        <input
            type="text"
            name="calculation-name"
            class="form-control"
            v-tooltip="'Zadejte stručný a výstižný název pro kalkulaci, název je viditelný pro zákazníka - propisuje se do předmětu nabídky / objednávky! Délka je omezena na max 120 znaků.'"
            v-model="localData.name"
            maxlength="120"
            @input="emitUpdate"
            v-validate="{
                   rules: { required: true},
                   message: { required: 'Název kalkulace je povinný'},
                   options: { showValidState: false }
                 }"
            disabled
        />
      </div>

      <!-- 2. Obchodní parametry -->
      <div class="row my-2">
        <div class="form-group col-md-2" :key="props.details.minProformaValue">
          <label class="form-label" for="proforma-absolute">Záloha vč. DPH v Kč</label>
          <input type="number"
                 :min="props.details.minProformaValue"
                 :max="props.maxProformaValue"
                 class="form-control"
                 id="proforma-absolute"
                 name="proforma-absolute"
                 placeholder="Zadej výši zálohy v Kč"
                 :value="proformaValueAbsolute"
                 @input="$emit('update:proformaValueAbsolute', Number($event.target.value))"
                 @validation="(event) => $emit('update:proformaValidation', event.detail.isValid)"
                 v-validate="{
                         rules: {
                           required: true,
                           min: props.details.minProformaValue,
                           max: props.maxProformaValue
                         },
                         message: {
                           required: 'Záloha je povinná',
                           min: `Minimálně ${props.details.minProformaValue} Kč`,
                           max: `Maximálně ${props.maxProformaValue} Kč`
                         },
                         options: { showValidState: false }
                 }">
        </div>
        <div class="form-group col-md-2">
          <label class="form-label" for="proforma">Záloha v %</label>
          <input type="number"
                 class="form-control"
                 id="proforma"
                 name="proforma"
                 :value="proformaValuePercent"
                 v-validate="{
                   rules: {
                     required: true,
                     min: 50,
                     max: 100
                   },
                   message: {
                     required: 'Záloha je povinná',
                     min: 'Záloha musí být minimálně 50%',
                     max: 'Záloha nesmí přesáhnout 100%'
                   },
                   options: { showValidState: false }
                 }"
                 disabled>
        </div>
        <div class="form-group col-md-4">
          <label class="form-label" for="delivery-time">Termín dodání:</label>
          <input type="text"
                 class="form-control"
                 id="delivery-time"
                 name="delivery-time"
                 v-model="localData.deliveryTime"
                 @input="emitUpdate"
                 v-validate="{
                         rules: { required: true },
                         message: { required: 'Termín dodání je povinný'},
                         options: { showValidState: false }
                       }">
        </div>
        <div class="form-group col-md-2">
          <label for="vat" class="form-label">Sazba DPH:</label>
          <select v-model="localData.vat"
                  class="form-select"
                  id="vat"
                  name="vat"
                  @change="emitUpdate"
                  v-validate="{
                         rules: { required: true },
                         message: { required: 'Sazba DPH je povinná'},
                         options: { showValidState: false }
                       }">
            <option value="-1" disabled>vyber DPH...</option>
            <option v-for="(text, key) in vatOptions" :key="key" :value="key">
              {{ text }}
            </option>
          </select>
        </div>
        <div class="form-group col-md-2 pb-2">
          <div class="form-check form-switch" style="margin-top: 30px">
            <label class="form-check-label" for="delegatedVat">Přenesená daň</label>
            <input class="form-check-input"
                   type="checkbox"
                   role="switch"
                   id="delegatedVat"
                   name="delegatedVat"
                   v-model="localData.delegatedVat"
                   @change="emitUpdate">
          </div>
        </div>
      </div>

      <!-- 3. Interní poznámka -->
      <div class="col-md-12">
        <label class="form-label">Interní poznámka:</label>
        <textarea class="form-control" rows="4" v-model="localData.internalNote" @input="emitUpdate"></textarea>
      </div>
    </div>
  </BaseCardSection>
</template>

<script setup>
import {reactive, computed, watch, defineProps, defineEmits, ref} from 'vue';
import BaseCardSection from "@/components/base/BaseCardSection.vue";

const fieldStatus = ref({});
const props = defineProps({
  details: {type: Object, required: true},
  proformaValueAbsolute: [Number, String],
  proformaValuePercent: [Number, String],
  maxProformaValue: [Number, String]
});

const emit = defineEmits([
  'update:details',
  'update:proformaValueAbsolute',
  'update:proformaValidation',
  'update:isSectionValid'
]);

const handleValidation = (event) => {
  const {fieldName, isValid} = event.detail;

  // Uložíme stav konkrétního pole
  fieldStatus.value[fieldName] = isValid;

  // Sekce je validní pouze pokud jsou VŠECHNA registrovaná pole true
  const isSectionValid = Object.values(fieldStatus.value).every(status => status === true);

  emit('update:isSectionValid', isSectionValid);
};

const localData = reactive({...props.details});

const vatOptions = {0: '0 %', 12: '12 %', 21: '21 %'};
const nameLength = computed(() => localData.name ? localData.name.length : 0);

const emitUpdate = () => {
  emit('update:details', {...localData});
};

watch(() => props.details, (newVal) => {
  Object.assign(localData, newVal);
}, {deep: true});
</script>